import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { libraryAsset } from "../../utils/libraryAssets";
import {
  LIBRARY_TABS,
  LIBRARY_HOW_STEPS,
  LIBRARY_NFT_TIERS,
  COLLECTOR_EDITION_PURCHASE_BLURB,
} from "../../data/libraryMockData";
import { getLibraryAssets, getLibraryAssetBySlug, getLibraryNftComicTiers, getMyLibrary } from "../../services/libraryApi";
import { createLibraryCheckoutSession, createLibraryNftMintCheckoutSession } from "../../services/libraryPurchaseApi";
import { useAuth } from "../../context/AuthContext";
import AuthModal from "../../components/auth/AuthModal";
import {
  setPendingLibraryPurchase,
  clearPendingLibraryPurchase,
  getPendingLibraryPurchase,
} from "../../utils/pendingLibraryPurchase";
import { clampNftCheckoutQuantity, effectiveMaxNftCheckoutForTier } from "../../utils/libraryNftPurchaseCaps";
import { LIBRARY_LISTING_SEO } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import LibraryNftWalletPurchaseControls from "../../components/library/LibraryNftWalletPurchaseControls";
import LibraryNftV2DirectMintControls from "../../components/library/LibraryNftV2DirectMintControls";
import LibraryPurchaseLegalModal from "../../components/legal/LibraryPurchaseLegalModal";
import { trackLibraryAssetView, trackLibraryView, trackNftComicCtaClick } from "../../utils/analytics";
import "./library.css";

const HERO_FALLBACK = libraryAsset("01_library_hero_great_hall.webp");
const NFT_STORE_HASH = "#store-nft";
const NFT_STORE_FOCUS_MS = 4200;
/** Genesis hub slug — listed on /library via nft-comic-tiers; may be excluded from catalog assets. */
const GENESIS_NFT_ASSET_SLUG = "treydora-genesis-comic";
const NFT_COLLECTOR_TIER_KEYS = ["merch_rights", "armory", "reader_access"];

function resolveEditionId(ed) {
  if (!ed) return "";
  if (ed._id != null && String(ed._id).trim() !== "") return String(ed._id);
  if (ed.id != null && String(ed.id).trim() !== "") return String(ed.id);
  return "";
}

function isNftCollectorEdition(ed) {
  if (!ed || ed.active === false) return false;
  const tk = String(ed.tierKey || "").trim();
  if (!NFT_COLLECTOR_TIER_KEYS.includes(tk)) return false;
  if (ed.editionType && ed.editionType !== "nft") return false;
  return true;
}

function pickNftCollectorEditions(editions) {
  return (Array.isArray(editions) ? editions : []).filter(isNftCollectorEdition);
}

/** Prefer API rows that include checkout id + price when merging tier sources. */
function mergeNftEditionIntoMap(byKey, ed) {
  if (!isNftCollectorEdition(ed)) return;
  const tk = String(ed.tierKey || "").trim();
  const prev = byKey[tk];
  const prevPrice = prev && Number.isFinite(Number(prev.priceUsd)) && Number(prev.priceUsd) > 0;
  const nextPrice = Number.isFinite(Number(ed.priceUsd)) && Number(ed.priceUsd) > 0;
  if (!prev || (!prevPrice && nextPrice) || (!resolveEditionId(prev) && resolveEditionId(ed))) {
    byKey[tk] = ed;
  }
}

function tabShows(tab, ...keys) {
  if (tab === "all") return true;
  return keys.includes(tab);
}

function resolveAssetImage(asset) {
  if (!asset) return HERO_FALLBACK;
  const c = (asset.coverImage && String(asset.coverImage).trim()) || "";
  const h = (asset.heroImage && String(asset.heroImage).trim()) || "";
  if (c) return c;
  if (h) return h;
  return HERO_FALLBACK;
}

/** Single neutral fallback when an asset has no cover/hero — not a separate “product”. */
const DIGITAL_CARD_IMAGE_FALLBACK = libraryAsset("00_library_page_master_mockup.webp");

/** Cinematic backdrop for upcoming: hero first, then cover, then neutral art. */
function resolveUpcomingBackdrop(asset) {
  if (!asset) return DIGITAL_CARD_IMAGE_FALLBACK;
  const h = (asset.heroImage && String(asset.heroImage).trim()) || "";
  const c = (asset.coverImage && String(asset.coverImage).trim()) || "";
  if (h) return h;
  if (c) return c;
  return DIGITAL_CARD_IMAGE_FALLBACK;
}

function formatUsd(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x < 0) return "—";
  if (x === 0) return "$0";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(x);
}

/** One storefront digital row per asset: cheapest active digital edition. */
function pickPrimaryDigitalEdition(editions) {
  const digital = (editions || []).filter((e) => e.editionType === "digital" && e.active !== false);
  if (!digital.length) return null;
  return [...digital].sort((a, b) => {
    const pa = Number(a.priceUsd);
    const pb = Number(b.priceUsd);
    const fa = Number.isFinite(pa) ? pa : Infinity;
    const fb = Number.isFinite(pb) ? pb : Infinity;
    if (fa !== fb) return fa - fb;
    return String(a.slug || "").localeCompare(String(b.slug || ""));
  })[0];
}

function upcomingDisplayTitle(a) {
  return String(a?.marketingHeadline || a?.title || "").trim() || "Untitled";
}

function upcomingTeaser(a) {
  const sub = String(a?.marketingSubheadline || "").trim();
  const hook = String(a?.marketingHook || "").trim();
  const short = String(a?.shortDescription || "").trim();
  const desc = String(a?.description || "").trim();
  return sub || hook || short || (desc.length > 240 ? `${desc.slice(0, 237)}…` : desc) || "";
}

/**
 * No waitlist API yet — opens the visitor’s mail client.
 * CRA (react-scripts): set `REACT_APP_LIBRARY_NOTIFY_EMAIL` in `.env`.
 */
function buildLibraryNotifyMailto(title) {
  const raw =
    (typeof process !== "undefined" &&
      process.env &&
      String(process.env.REACT_APP_LIBRARY_NOTIFY_EMAIL || "").trim()) ||
    "library@treydora.com";
  const addr = String(raw || "library@treydora.com").replace(/^mailto:/i, "");
  const sub = encodeURIComponent(`Treydora Library — notify me: ${title}`);
  return `mailto:${addr}?subject=${sub}`;
}

function UpcomingVaultCard({ asset, variant, navigate, buildNotifyMailto, resolveBackdrop, getTitle, getTeaser }) {
  const slug = String(asset.slug || "").trim().toLowerCase();
  const bg = resolveBackdrop(asset);
  const title = getTitle(asset);
  const te = getTeaser(asset);
  const hookBullets = Array.isArray(asset.marketingBullets)
    ? asset.marketingBullets.map((b) => String(b || "").trim()).filter(Boolean).slice(0, 3)
    : [];
  const rootClass =
    variant === "feature" ? "lib-up-vault lib-up-vault--feature" : "lib-up-vault lib-up-vault--grid";

  return (
    <article className={rootClass}>
      <div className="lib-up-vault__bg" style={{ backgroundImage: `url(${bg})` }} role="presentation" />
      <div className="lib-up-vault__gradient" aria-hidden />
      <div className="lib-up-vault__gridlines" aria-hidden />
      <div className="lib-up-vault__body">
        <ul className="lib-up-vault__badges" aria-label="Release labels">
          <li className="lib-up-vault__badge lib-up-vault__badge--gold">Coming Soon</li>
          <li className="lib-up-vault__badge">Future Release</li>
          <li className="lib-up-vault__badge lib-up-vault__badge--blue">Archive Horizon</li>
          <li className="lib-up-vault__badge">Notify List</li>
        </ul>
        <h3 className="lib-up-vault__title lib-serif">{title}</h3>
        {asset.series || asset.character ? (
          <p className="lib-up-vault__meta">
            {[asset.series, asset.character].filter(Boolean).join(" · ")}
          </p>
        ) : null}
        {te ? <p className="lib-up-vault__teaser">{te}</p> : null}
        {hookBullets.length ? (
          <ul className="lib-up-vault__mini-bullets">
            {hookBullets.map((b, i) => (
              <li key={`${slug}-b${i}`}>{b}</li>
            ))}
          </ul>
        ) : null}
        <div className="lib-up-vault__ctas">
          <a className="lib-btn lib-up-vault__cta lib-up-vault__cta--notify" href={buildNotifyMailto(title)}>
            Notify Me
          </a>
          <button
            type="button"
            className="lib-btn lib-up-vault__cta lib-up-vault__cta--lore"
            disabled={!slug}
            onClick={() => {
              if (!slug) return;
              trackLibraryAssetView({
                cta_id: "upcoming_preview_lore",
                section: "upcoming_vault",
                asset_slug: slug,
              });
              navigate(`/library/${encodeURIComponent(slug)}`);
            }}
          >
            Preview Lore
          </button>
        </div>
      </div>
    </article>
  );
}

export default function LibraryPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const purchaseCancelled = searchParams.get("purchase") === "cancelled";

  const [tab, setTab] = useState("all");
  const [catalog, setCatalog] = useState([]);
  const [detailsBySlug, setDetailsBySlug] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [emptyCatalog, setEmptyCatalog] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [purchasingEditionId, setPurchasingEditionId] = useState("");
  const [alreadyOwnedInfo, setAlreadyOwnedInfo] = useState(null);
  const prevAuthRef = useRef(null);
  const [nftPublicEditions, setNftPublicEditions] = useState([]);
  const [nftGenesisSlug, setNftGenesisSlug] = useState("");
  const [ownedNftEditionIds, setOwnedNftEditionIds] = useState(() => new Set());
  const [ownedDigitalEditionIds, setOwnedDigitalEditionIds] = useState(() => new Set());
  const [digitalCoverBrokenByEdition, setDigitalCoverBrokenByEdition] = useState({});
  const [nftQtyByEdition, setNftQtyByEdition] = useState({});
  const [nftStoreFocusPulse, setNftStoreFocusPulse] = useState(false);
  const nftStoreFocusTimerRef = useRef(null);
  const [legalAck, setLegalAck] = useState({
    libraryDigitalOk: false,
    libraryNftOk: false,
    vrPurchaseOk: false,
  });
  const [libraryLegalModalOpen, setLibraryLegalModalOpen] = useState(false);
  const [libraryLegalModalKind, setLibraryLegalModalKind] = useState("digital");
  const libraryLegalPendingRef = useRef(null);
  const walletEthResumeByEditionRef = useRef({});
  const walletV2ResumeByEditionRef = useRef({});

  const openLibraryLegalModal = (kind, pending) => {
    libraryLegalPendingRef.current = pending;
    setLibraryLegalModalKind(kind);
    setLibraryLegalModalOpen(true);
  };

  const openWalletNftLegal = (editionId, resumeRail) => {
    clearPendingLibraryPurchase();
    setCheckoutError("");
    openLibraryLegalModal("nft", {
      editionId: String(editionId || "").trim(),
      resumeRail,
    });
  };

  const startDigitalCheckout = async (editionId, ctx = {}) => {
    const eid = String(editionId || "").trim();
    if (!eid) return;
    setAlreadyOwnedInfo(null);
    if (!isAuthenticated) {
      setPendingLibraryPurchase({
        editionId: eid,
        assetSlug: ctx.assetSlug || "",
        assetId: ctx.assetId || "",
        purchaseKind: "digital",
        returnPath: `${window.location.pathname}${window.location.search}`,
        authGate: "email",
      });
      setAuthOpen(true);
      return;
    }
    if (!legalAck.libraryDigitalOk) {
      openLibraryLegalModal("digital", { editionId: eid, ctx, resumeRail: "stripe_digital" });
      return;
    }
    setCheckoutError("");
    setPurchasingEditionId(eid);
    try {
      const result = await createLibraryCheckoutSession(eid);
      clearPendingLibraryPurchase();
      if (result?.alreadyOwned) {
        setAlreadyOwnedInfo(result);
        return;
      }
      if (result?.url) window.location.assign(result.url);
    } catch (err) {
      setCheckoutError(err?.message || "Checkout could not be started.");
    } finally {
      setPurchasingEditionId("");
    }
  };

  const startNftMintCheckout = async (editionId, ctx = {}) => {
    const eid = String(editionId || "").trim();
    if (!eid) return;
    setAlreadyOwnedInfo(null);
    const tierKey = String(ctx.tierKey || "").trim();
    const rawQty = ctx.quantity != null ? ctx.quantity : nftQtyByEdition[eid];
    const qty = clampNftCheckoutQuantity(tierKey, rawQty ?? 1, ctx.inventoryRemaining);
    if (!isAuthenticated) {
      setPendingLibraryPurchase({
        editionId: eid,
        assetSlug: ctx.assetSlug || "",
        assetId: ctx.assetId || "",
        purchaseKind: "nft_mint",
        returnPath: `${window.location.pathname}${window.location.search}`,
        quantity: qty,
        tierKey,
        authGate: "email",
      });
      setAuthOpen(true);
      return;
    }
    if (!legalAck.libraryNftOk) {
      openLibraryLegalModal("nft", { editionId: eid, ctx, resumeRail: "stripe_nft" });
      return;
    }
    setCheckoutError("");
    setPurchasingEditionId(eid);
    try {
      const result = await createLibraryNftMintCheckoutSession(eid, qty);
      clearPendingLibraryPurchase();
      if (result?.alreadyOwned) {
        setAlreadyOwnedInfo(result);
        return;
      }
      if (result?.url) window.location.assign(result.url);
    } catch (err) {
      setCheckoutError(err?.message || "Checkout could not be started.");
    } finally {
      setPurchasingEditionId("");
    }
  };

  const handleLibraryLegalModalContinue = () => {
    const kind = libraryLegalModalKind;
    const pending = libraryLegalPendingRef.current;
    const ok = kind === "nft" ? legalAck.libraryNftOk : legalAck.libraryDigitalOk;
    if (!ok) return;
    setLibraryLegalModalOpen(false);
    libraryLegalPendingRef.current = null;

    if (pending?.resumeRail === "wallet_eth") {
      walletEthResumeByEditionRef.current[pending.editionId]?.();
      return;
    }
    if (pending?.resumeRail === "wallet_v2") {
      walletV2ResumeByEditionRef.current[pending.editionId]?.();
      return;
    }
    if (!pending?.editionId) return;
    if (kind === "nft") void startNftMintCheckout(pending.editionId, pending.ctx || {});
    else void startDigitalCheckout(pending.editionId, pending.ctx || {});
  };

  useEffect(() => {
    if (purchaseCancelled) clearPendingLibraryPurchase();
  }, [purchaseCancelled]);

  useEffect(() => {
    if (!isAuthenticated) {
      prevAuthRef.current = false;
      return;
    }
    const wasLoggedOut = prevAuthRef.current === false;
    prevAuthRef.current = true;
    if (!wasLoggedOut) return;

    const pending = getPendingLibraryPurchase();
    if (!pending?.editionId) return;
    if (pending.authGate !== "email") {
      clearPendingLibraryPurchase();
      return;
    }

    const needsLegal =
      pending.purchaseKind === "nft_mint" ? legalAck.libraryNftOk : legalAck.libraryDigitalOk;
    if (!needsLegal) {
      setCheckoutError("");
      openLibraryLegalModal(pending.purchaseKind === "nft_mint" ? "nft" : "digital", {
        editionId: String(pending.editionId),
        resumeRail: pending.purchaseKind === "nft_mint" ? "stripe_nft" : "stripe_digital",
        ctx: {
          assetSlug: pending.assetSlug || "",
          assetId: pending.assetId || "",
          quantity: pending.quantity,
          tierKey: pending.tierKey || "",
        },
      });
      setPurchasingEditionId("");
      return;
    }

    let cancelled = false;
    (async () => {
      setCheckoutError("");
      setPurchasingEditionId(String(pending.editionId));
      try {
        const isNft = pending.purchaseKind === "nft_mint";
        const pendingQty = clampNftCheckoutQuantity(
          pending.tierKey || "",
          pending.quantity != null ? pending.quantity : 1
        );
        const result = isNft
          ? await createLibraryNftMintCheckoutSession(String(pending.editionId), pendingQty)
          : await createLibraryCheckoutSession(String(pending.editionId));
        if (cancelled) return;
        clearPendingLibraryPurchase();
        setAuthOpen(false);
        if (result?.alreadyOwned) {
          setAlreadyOwnedInfo(result);
          return;
        }
        if (result?.url) window.location.assign(result.url);
      } catch (e) {
        if (!cancelled) setCheckoutError(e?.message || "Checkout could not be started.");
      } finally {
        if (!cancelled) setPurchasingEditionId("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, legalAck.libraryDigitalOk, legalAck.libraryNftOk]);

  usePageSeo(LIBRARY_LISTING_SEO);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      setEmptyCatalog(false);
      try {
        const data = await getLibraryAssets({ limit: 100 });
        const items = Array.isArray(data?.items) ? data.items : [];
        if (cancelled) return;
        setCatalog(items);
        if (!items.length) {
          setEmptyCatalog(true);
          setDetailsBySlug({});
          return;
        }
        const published = items.filter(
          (a) => a.status === "published" && a.publicStorefront !== false
        );
        const pairs = await Promise.all(
          published.map(async (a) => {
            const slugKey = String(a.slug || "").trim().toLowerCase();
            if (!slugKey) return [null, null];
            try {
              const d = await getLibraryAssetBySlug(slugKey);
              return [slugKey, d];
            } catch {
              return [slugKey, null];
            }
          })
        );
        if (cancelled) return;
        const map = {};
        pairs.forEach(([slugKey, d]) => {
          if (slugKey && d && d.asset) map[slugKey] = d;
        });
        setDetailsBySlug(map);
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setCatalog([]);
          setDetailsBySlug({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let editions = [];
      let slug = GENESIS_NFT_ASSET_SLUG;

      try {
        const d = await getLibraryNftComicTiers();
        editions = pickNftCollectorEditions(d?.editions);
        slug = String(d?.assetSlug || GENESIS_NFT_ASSET_SLUG).trim().toLowerCase() || GENESIS_NFT_ASSET_SLUG;
      } catch {
        /* fall through to genesis asset-by-slug */
      }

      if (!editions.length) {
        try {
          const pack = await getLibraryAssetBySlug(slug || GENESIS_NFT_ASSET_SLUG);
          editions = pickNftCollectorEditions(pack?.editions);
          slug = String(pack?.asset?.slug || slug || GENESIS_NFT_ASSET_SLUG)
            .trim()
            .toLowerCase();
        } catch {
          /* keep empty */
        }
      }

      if (cancelled) return;
      setNftPublicEditions(editions);
      setNftGenesisSlug(slug || GENESIS_NFT_ASSET_SLUG);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Genesis hub may be excluded from catalog list; preload detail pack for tier merge fallback. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pack = await getLibraryAssetBySlug(GENESIS_NFT_ASSET_SLUG);
        if (cancelled || !pack?.asset) return;
        setDetailsBySlug((prev) => ({
          ...prev,
          [GENESIS_NFT_ASSET_SLUG]: pack,
        }));
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setOwnedNftEditionIds(new Set());
      setOwnedDigitalEditionIds(new Set());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await getMyLibrary();
        const nftSet = new Set();
        const digitalSet = new Set();
        (Array.isArray(d?.items) ? d.items : []).forEach((row) => {
          const id = row?.edition?._id != null ? String(row.edition._id) : "";
          if (!id) return;
          if (row?.edition?.editionType === "nft") nftSet.add(id);
          if (row?.edition?.editionType === "digital") digitalSet.add(id);
        });
        if (!cancelled) {
          setOwnedNftEditionIds(nftSet);
          setOwnedDigitalEditionIds(digitalSet);
        }
      } catch {
        if (!cancelled) {
          setOwnedNftEditionIds(new Set());
          setOwnedDigitalEditionIds(new Set());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const primaryPublishedSlug = useMemo(() => {
    const pub = catalog.filter((a) => a.status === "published" && a.publicStorefront !== false);
    const featured = pub.find((a) => a.featured) || pub.find((a) => a.type === "comic") || pub[0];
    return featured?.slug ? String(featured.slug).trim().toLowerCase() : "";
  }, [catalog]);

  /**
   * One card per LibraryAsset from GET /api/library/assets (published + storefront).
   * Detail packs are keyed by slug; iterate catalog so we never render orphan/mock rows.
   */
  const digitalRows = useMemo(() => {
    const published = catalog
      .filter((a) => a.status === "published" && a.publicStorefront !== false)
      .sort((a, b) => {
        const o = (a.sortOrder || 0) - (b.sortOrder || 0);
        if (o !== 0) return o;
        return String(a.title || "").localeCompare(String(b.title || ""));
      });

    const seen = new Set();
    const rows = [];
    for (const listAsset of published) {
      const slugKey = String(listAsset.slug || "").trim().toLowerCase();
      if (!slugKey) continue;
      const dedupeId = listAsset._id != null ? String(listAsset._id) : slugKey;
      if (seen.has(dedupeId)) continue;

      const pack = detailsBySlug[slugKey];
      const asset = pack?.asset;
      if (!asset || asset.status !== "published" || asset.publicStorefront === false) continue;

      const eds = Array.isArray(pack.editions) ? pack.editions : [];
      const primary = pickPrimaryDigitalEdition(eds);
      if (!primary) continue;

      seen.add(dedupeId);
      const digitalCount = eds.filter((e) => e.editionType === "digital" && e.active !== false).length;
      rows.push({ asset, edition: primary, digitalEditionCount: digitalCount });
    }
    return rows;
  }, [catalog, detailsBySlug]);

  /** Public storefront upcoming titles (same rules as GET /api/library/assets). */
  const upcomingSorted = useMemo(() => {
    return catalog
      .filter((a) => a.status === "coming_soon" && a.publicStorefront !== false)
      .sort((a, b) => {
        const o = (a.sortOrder || 0) - (b.sortOrder || 0);
        if (o !== 0) return o;
        return String(a.title || "").localeCompare(String(b.title || ""));
      });
  }, [catalog]);

  const upcomingFeatured = useMemo(() => {
    const hit = upcomingSorted.find((a) => a.featured);
    return hit || null;
  }, [upcomingSorted]);

  const upcomingGridItems = useMemo(() => {
    if (!upcomingFeatured) return upcomingSorted;
    const fid = upcomingFeatured._id != null ? String(upcomingFeatured._id) : "";
    return upcomingSorted.filter((a) => (a._id != null ? String(a._id) : "") !== fid);
  }, [upcomingSorted, upcomingFeatured]);

  const filteredDigitalRows = useMemo(() => {
    if (tab === "all") return digitalRows;
    if (tab === "digital_comics") {
      return digitalRows.filter(({ edition }) => edition.tierKey === "digital_comic");
    }
    if (tab === "books") {
      return digitalRows.filter(({ edition }) => edition.tierKey === "digital_book");
    }
    return digitalRows;
  }, [digitalRows, tab]);

  const showNftBlock = useMemo(() => tabShows(tab, "nft_comics"), [tab]);

  const mergedNftTiers = useMemo(() => {
    const staticList = LIBRARY_NFT_TIERS.filter(
      (t) => tab === "all" || (Array.isArray(t.tabIds) && t.tabIds.includes(tab))
    );
    const byKey = {};
    pickNftCollectorEditions(nftPublicEditions).forEach((e) => mergeNftEditionIntoMap(byKey, e));

    const slugKeys = [
      nftGenesisSlug,
      primaryPublishedSlug,
      GENESIS_NFT_ASSET_SLUG,
    ]
      .map((s) => String(s || "").trim().toLowerCase())
      .filter(Boolean);
    for (const sk of [...new Set(slugKeys)]) {
      const pack = detailsBySlug[sk];
      if (pack?.editions) pickNftCollectorEditions(pack.editions).forEach((e) => mergeNftEditionIntoMap(byKey, e));
    }

    return staticList.map((t) => ({ ...t, edition: byKey[String(t.tierKey)] || null }));
  }, [tab, nftPublicEditions, detailsBySlug, nftGenesisSlug, primaryPublishedSlug]);

  const showHow = useMemo(() => tabShows(tab, "nft_comics"), [tab]);

  /** Show Digital Editions block (real rows or empty state) — never merge mock products. */
  const showDigitalSection = useMemo(() => tabShows(tab, "digital_comics", "books"), [tab]);

  const showMerchLegalCheckbox = useMemo(() => {
    return mergedNftTiers.some((tier) => {
      const ed = tier.edition;
      if (!ed) return false;
      const eid = resolveEditionId(ed);
      const remRaw = ed?.nftInventoryRemaining;
      const remNum =
        remRaw != null && Number.isFinite(Number(remRaw)) ? Math.max(0, Math.floor(Number(remRaw))) : null;
      const invExhausted = remNum === 0;
      const priceOk = Number.isFinite(Number(ed.priceUsd)) && Number(ed.priceUsd) > 0;
      const saleOn = ed.saleActive !== false;
      return Boolean(eid && priceOk && saleOn && !invExhausted);
    });
  }, [mergedNftTiers]);

  const showUpcomingSection = useMemo(() => tab === "all" || tab === "coming_soon", [tab]);

  const wantsNftStoreHash = useMemo(
    () => String(location.hash || "").trim().toLowerCase() === NFT_STORE_HASH,
    [location.hash]
  );

  const catalogReady = !loading && !loadError && !emptyCatalog;
  /** Collector editions use static tier layout + GET nft-comic-tiers; not gated on catalog fetch. */
  const nftStoreSectionReady = showNftBlock && mergedNftTiers.length > 0;

  /** Deep links from My Library use #store-nft; ensure NFT tab is visible. */
  useEffect(() => {
    if (!wantsNftStoreHash) return;
    if (!tabShows(tab, "nft_comics")) {
      setTab("all");
    }
  }, [wantsNftStoreHash, tab]);

  /**
   * Retry scroll/highlight after catalog renders #store-nft (global hashScroll only runs once).
   */
  useEffect(() => {
    if (!wantsNftStoreHash || !nftStoreSectionReady) return undefined;

    let cancelled = false;
    let raf2 = 0;

    const pulseHighlight = () => {
      setNftStoreFocusPulse(true);
      if (nftStoreFocusTimerRef.current) clearTimeout(nftStoreFocusTimerRef.current);
      nftStoreFocusTimerRef.current = setTimeout(() => {
        setNftStoreFocusPulse(false);
        nftStoreFocusTimerRef.current = null;
      }, NFT_STORE_FOCUS_MS);
    };

    const focusNftStoreSection = () => {
      if (cancelled) return;
      const el = document.getElementById("store-nft");
      if (!el) return;

      el.scrollIntoView({ behavior: "smooth", block: "start" });

      const heading = document.getElementById("store-nft-heading");
      if (heading) {
        if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
        heading.focus({ preventScroll: true });
      }

      pulseHighlight();
    };

    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(focusNftStoreSection);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [wantsNftStoreHash, nftStoreSectionReady, tab, mergedNftTiers.length]);

  useEffect(
    () => () => {
      if (nftStoreFocusTimerRef.current) clearTimeout(nftStoreFocusTimerRef.current);
    },
    []
  );

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (id === "store-nft") {
      setNftStoreFocusPulse(true);
      if (nftStoreFocusTimerRef.current) clearTimeout(nftStoreFocusTimerRef.current);
      nftStoreFocusTimerRef.current = setTimeout(() => {
        setNftStoreFocusPulse(false);
        nftStoreFocusTimerRef.current = null;
      }, NFT_STORE_FOCUS_MS);
    }
  };

  const trackAndScrollLibrary = (ctaId, targetId, nft = false) => {
    const params = {
      cta_id: ctaId,
      section: "library_hero",
      destination: `#${targetId}`,
    };

    if (nft) trackNftComicCtaClick(params);
    else trackLibraryView(params);

    scrollToId(targetId);
  };

  const heroBackground = HERO_FALLBACK;

  const nftDetailsPath = useMemo(() => {
    const s = String(nftGenesisSlug || primaryPublishedSlug || "").trim().toLowerCase();
    return s ? `/library/${encodeURIComponent(s)}` : "/library";
  }, [nftGenesisSlug, primaryPublishedSlug]);

  return (
    <div className="lib-storefront">
      <section
        className="lib-hero lib-hero--storefront"
        aria-labelledby="lib-hero-title"
        style={{ "--lib-hero-img": `url(${heroBackground})` }}
      >
        <div className="lib-hero__bg" aria-hidden />
        <div className="lib-hero__vignette" aria-hidden />
        <div className="lib-hero__grid" aria-hidden />
        <div className="lib-hero__inner">
          <div className="lib-hero__label lib-hud">Treydora Archive</div>
          <h1 id="lib-hero-title" className="lib-hero__title lib-serif">
            The Treydora Library
          </h1>
          <p className="lib-hero__subtitle">Read the stories. Own the relics. Enter the world.</p>
          <p className="lib-hero__lede">
            The public archive storefront for Treydora — digital comic collector editions, instant digital editions, and
            upcoming saga releases. Every path here leads deeper into Oshar.
          </p>
          <div className="lib-hero__actions">
            <button
              type="button"
              className="lib-btn lib-btn--gold lib-btn--hero"
              onClick={() => trackAndScrollLibrary("library_hero_explore_collector_editions", "store-nft", true)}
            >
              Explore Collector Editions
            </button>
            <button
              type="button"
              className="lib-btn lib-btn--primary lib-btn--hero"
              onClick={() => trackAndScrollLibrary("library_hero_browse_digital_editions", "store-digital")}
            >
              Browse Digital Editions
            </button>
          </div>
          <p className="lib-hero__vault">
            <Link to="/dashboard/library" className="lib-hero__vault-link">
              View My Library — personal vault
            </Link>
          </p>
        </div>
      </section>

      <div className="lib-rule" aria-hidden />

      <div className="lib-inner" id="library-catalog">
        <>
            {!loading && loadError ? (
              <div className="lib-catalog-state" style={{ padding: "0 0 28px", textAlign: "center" }}>
                <p className="lib-sec-desc lib-sec-desc--narrow" style={{ margin: 0 }}>
                  Additional catalog listings could not be reached. Collector editions below are still available.
                </p>
              </div>
            ) : null}
            {!loading && emptyCatalog && !loadError ? (
              <div className="lib-catalog-state" style={{ padding: "0 0 28px", textAlign: "center" }}>
                <p className="lib-sec-desc lib-sec-desc--narrow" style={{ margin: 0 }}>
                  More digital and upcoming archive titles are being prepared. Collector editions are open below.
                </p>
              </div>
            ) : null}
            {purchaseCancelled ? (
              <p
                className="lib-sec-desc"
                role="status"
                style={{ margin: "0 0 20px", padding: "12px 16px", border: "1px solid var(--lib-border-subtle)", borderRadius: 2 }}
              >
                Checkout was cancelled. No charge was made.
              </p>
            ) : null}
            {checkoutError ? (
              <p className="lib-sec-desc" role="alert" style={{ margin: "0 0 16px", color: "#ffb4a8" }}>
                {checkoutError}
              </p>
            ) : null}
            {alreadyOwnedInfo?.alreadyOwned ? (
              <div
                className="lib-sec-desc"
                role="status"
                style={{
                  margin: "0 0 20px",
                  padding: "14px 18px",
                  border: "1px solid rgba(110, 231, 183, 0.35)",
                  borderRadius: 8,
                  background: "rgba(6, 40, 32, 0.45)",
                }}
              >
                <p style={{ margin: "0 0 10px", fontWeight: 600 }}>You already own this edition.</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  <Link to="/dashboard/library" className="lib-btn lib-btn--gold lib-btn--sm" style={{ textDecoration: "none" }}>
                    Open My Library
                  </Link>
                  {alreadyOwnedInfo.readUrl && String(alreadyOwnedInfo.readUrl).startsWith("/") ? (
                    <Link to={alreadyOwnedInfo.readUrl} className="lib-btn lib-btn--primary lib-btn--sm" style={{ textDecoration: "none" }}>
                      Open reader
                    </Link>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className="lib-filter-wrap">
              <p className="lib-filter-label">Browse by category</p>
              <div className="lib-tabs" role="tablist" aria-label="Storefront filters">
                {LIBRARY_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    className={`lib-tab${tab === t.id ? " is-active" : ""}`}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {showNftBlock && mergedNftTiers.length ? (
              <section
                className={`lib-sec lib-sec--nft${nftStoreFocusPulse ? " lib-sec--nft-focus" : ""}`}
                id="store-nft"
                aria-labelledby="store-nft-heading"
              >
                <header className="lib-sec-head">
                  <span className="lib-sec-eyebrow">Flagship release</span>
                  <h2 id="store-nft-heading" className="lib-sec-title lib-serif">
                    Treydora Genesis Comic Collector Editions
                  </h2>
                  <p className="lib-sec-product lib-serif">Digital collector editions · protected reader access</p>
                  <p className="lib-sec-desc">
                    Buy with card for instant comic access. Merch Rights, Armory, and Reader Access tiers — unique cover
                    art per edition; full interior in the Treydora reader. Wallet claim for your on-chain collectible is
                    available from My Library after purchase.
                  </p>
                </header>

                <div className="lib-edition-grid">
                  {mergedNftTiers.map((tier) => {
                    const ed = tier.edition;
                    const eid = resolveEditionId(ed);
                    const tierKey = String(ed?.tierKey || tier.tierKey || "").trim();
                    const remRaw = ed?.nftInventoryRemaining;
                    const remNum =
                      remRaw != null && Number.isFinite(Number(remRaw)) ? Math.max(0, Math.floor(Number(remRaw))) : null;
                    const invExhausted = remNum === 0;
                    const priceOk = ed && Number.isFinite(Number(ed.priceUsd)) && Number(ed.priceUsd) > 0;
                    const owned = eid && ownedNftEditionIds.has(eid);
                    const saleOn = ed && ed.saleActive !== false;
                    const displayedQty = clampNftCheckoutQuantity(tierKey, nftQtyByEdition[eid] ?? 1, remNum);
                    const maxCheckout = effectiveMaxNftCheckoutForTier(tierKey, remNum);
                    let goldLabel = "Edition unavailable";
                    let goldDisabled = true;
                    let goldOnClick = null;
                    /* Repeat collector-edition purchases: same Stripe edition can be bought multiple times. */
                    if (eid && !saleOn) {
                      goldLabel = "Coming Soon";
                      goldDisabled = true;
                    } else if (eid && priceOk && saleOn && invExhausted) {
                      goldLabel = "Sold out";
                      goldDisabled = true;
                      goldOnClick = null;
                    } else if (eid && priceOk && saleOn) {
                      goldLabel =
                        purchasingEditionId === eid
                          ? "Preparing checkout…"
                          : owned
                            ? displayedQty > 1
                              ? "Buy More Editions"
                              : "Buy Another Edition"
                            : "Buy with Card";
                      goldDisabled = Boolean(purchasingEditionId);
                      goldOnClick = () =>
                        void startNftMintCheckout(eid, {
                          assetSlug: nftGenesisSlug || "",
                          tierKey,
                          quantity: displayedQty,
                          inventoryRemaining: remNum,
                        });
                    } else if (eid && !priceOk) {
                      goldLabel = "Pricing unavailable";
                      goldDisabled = true;
                      goldOnClick = null;
                    }
                    return (
                      <article key={tier.id} className={`lib-edition lib-edition--${tier.tierRank}`}>
                        <div className="lib-edition__frame">
                          <div
                            className="lib-edition__art"
                            style={{ backgroundImage: `url(${libraryAsset(tier.imageFile)})` }}
                            role="img"
                            aria-hidden
                          />
                          <div className="lib-edition__art-scrim" aria-hidden />
                          <span className="lib-edition__ribbon">{tier.supplyLabel}</span>
                        </div>
                        <div className="lib-edition__body">
                          <h3 className="lib-edition__name lib-serif">{tier.name}</h3>
                          <p className="lib-edition__tagline">{tier.tagline}</p>
                          <p className="lib-edition__purchase-note">{COLLECTOR_EDITION_PURCHASE_BLURB}</p>
                          <ul className="lib-edition__bullets">
                            {(Array.isArray(tier.benefits) ? tier.benefits : []).map((b) => (
                              <li key={b}>{b}</li>
                            ))}
                          </ul>
                          <p className="lib-digital-card__price" style={{ margin: "0 0 4px", opacity: 0.85 }}>
                            {priceOk ? `${formatUsd(ed.priceUsd)} each` : "Mint pricing at launch"}
                          </p>
                          {remNum != null ? (
                            <p className="lib-sec-desc" style={{ margin: "0 0 12px", fontSize: "0.78rem", opacity: 0.85 }}>
                              <strong>{remNum.toLocaleString()} remain</strong>
                              {ed?.nftInventoryTierMaxSlots != null && Number.isFinite(Number(ed.nftInventoryTierMaxSlots))
                                ? ` · tier capacity ${Number(ed.nftInventoryTierMaxSlots).toLocaleString()}`
                                : ""}
                            </p>
                          ) : ed?.supplyLimit != null && Number.isFinite(Number(ed.supplyLimit)) ? (
                            <p className="lib-sec-desc" style={{ margin: "0 0 12px", fontSize: "0.78rem", opacity: 0.75 }}>
                              Limited to {Number(ed.supplyLimit).toLocaleString()} editions
                            </p>
                          ) : (
                            <div style={{ marginBottom: 12 }} />
                          )}
                          {eid && priceOk && saleOn && !invExhausted ? (
                            <div className="lib-nft-qty" aria-label="Purchase quantity">
                              <span className="lib-nft-qty__label">Quantity</span>
                              <div className="lib-nft-qty__btns">
                                <button
                                  type="button"
                                  className="lib-nft-qty__step"
                                  aria-label="Decrease quantity"
                                  disabled={goldDisabled || displayedQty <= 1}
                                  onClick={() =>
                                    setNftQtyByEdition((prev) => ({
                                      ...prev,
                                      [eid]: clampNftCheckoutQuantity(tierKey, (prev[eid] ?? 1) - 1, remNum),
                                    }))
                                  }
                                >
                                  −
                                </button>
                                <span className="lib-nft-qty__value" aria-live="polite">
                                  {displayedQty}
                                </span>
                                <button
                                  type="button"
                                  className="lib-nft-qty__step"
                                  aria-label="Increase quantity"
                                  disabled={goldDisabled || displayedQty >= maxCheckout}
                                  onClick={() =>
                                    setNftQtyByEdition((prev) => ({
                                      ...prev,
                                      [eid]: clampNftCheckoutQuantity(tierKey, (prev[eid] ?? 1) + 1, remNum),
                                    }))
                                  }
                                >
                                  +
                                </button>
                              </div>
                              <span className="lib-nft-qty__total">
                                Total: {formatUsd(Number(ed.priceUsd) * displayedQty)}
                              </span>
                              <span className="lib-nft-qty__cap">Max {maxCheckout} per checkout</span>
                            </div>
                          ) : null}
                          <div className="lib-edition__cta">
                            <button
                              type="button"
                              className="oshar-btn-outline oshar-re-btn-sm"
                              onClick={() => {
                                trackNftComicCtaClick({
                                  cta_id: "nft_collector_view_details",
                                  section: "nft_collector_editions",
                                  destination: nftDetailsPath,
                                });
                                navigate(nftDetailsPath);
                              }}
                            >
                              {tier.ctaDetails || "View Details"}
                            </button>
                            {owned ? (
                              <button
                                type="button"
                                className="oshar-btn-outline oshar-re-btn-sm"
                                onClick={() => navigate("/dashboard/library")}
                              >
                                My Library
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="lib-btn lib-btn--gold lib-btn--sm"
                              disabled={goldDisabled}
                              onClick={goldOnClick || undefined}
                            >
                              {goldLabel}
                            </button>
                          </div>
                          {eid && priceOk && saleOn && !invExhausted ? (
                            <LibraryNftWalletPurchaseControls
                              editionId={eid}
                              quantity={displayedQty}
                              disabled={Boolean(purchasingEditionId)}
                              purchaseAllowed={legalAck.libraryNftOk}
                              onLegalAcknowledgmentRequired={() => openWalletNftLegal(eid, "wallet_eth")}
                              registerWalletResume={(fn) => {
                                if (fn) walletEthResumeByEditionRef.current[eid] = fn;
                                else delete walletEthResumeByEditionRef.current[eid];
                              }}
                              compact
                            />
                          ) : null}
                          {eid && priceOk && saleOn && !invExhausted ? (
                            <LibraryNftV2DirectMintControls
                              disabled={Boolean(purchasingEditionId)}
                              purchaseAllowed={legalAck.libraryNftOk}
                              onLegalAcknowledgmentRequired={() => openWalletNftLegal(eid, "wallet_v2")}
                              registerWalletResume={(fn) => {
                                if (fn) walletV2ResumeByEditionRef.current[eid] = fn;
                                else delete walletV2ResumeByEditionRef.current[eid];
                              }}
                              compact
                            />
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {showHow ? (
              <section className="lib-sec lib-sec--how" id="store-how" aria-labelledby="store-how-heading">
                <header className="lib-sec-head lib-sec-head--center">
                  <span className="lib-sec-eyebrow">How Treydora comics work</span>
                  <h2 id="store-how-heading" className="lib-sec-title lib-serif">
                    From edition to reader
                  </h2>
                  <p className="lib-sec-desc lib-sec-desc--narrow">
                    Your collector edition is not the entire comic file on its own. Each edition includes unique cover art;
                    the full interior lives in Treydora&apos;s protected viewer — front cover, pages, then back cover. Claim your
                    on-chain collectible from My Library when you are ready.
                  </p>
                </header>
                <ol className="lib-how-grid">
                  {LIBRARY_HOW_STEPS.map((s) => (
                    <li key={s.step} className="lib-how-step">
                      <span className="lib-how-step__num" aria-hidden>
                        {s.step}
                      </span>
                      <h3 className="lib-how-step__title lib-serif">{s.title}</h3>
                      <p className="lib-how-step__copy">{s.copy}</p>
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}

            {showDigitalSection ? (
              <section className="lib-sec lib-sec--digital" id="store-digital" aria-labelledby="store-digital-heading">
                <header className="lib-sec-head lib-sec-head--digital-store">
                  <span className="lib-sec-eyebrow lib-sec-eyebrow--blue">Vault storefront</span>
                  <h2 id="store-digital-heading" className="lib-sec-title lib-serif">
                    Digital editions
                  </h2>
                  <p className="lib-sec-desc">
                    Instant access after checkout — no wallet required. Secure payment via Stripe; your reader unlocks in
                    My Library.
                  </p>
                </header>
                {loading ? (
                  <div className="lib-catalog-state" role="status" style={{ padding: "28px 0 48px", textAlign: "center" }}>
                    <p className="lib-sec-desc lib-sec-desc--narrow" style={{ margin: 0 }}>
                      Opening the Treydora Library...
                    </p>
                  </div>
                ) : loadError ? (
                  <div className="lib-catalog-state" style={{ padding: "28px 0 48px", textAlign: "center" }}>
                    <p className="lib-sec-desc lib-sec-desc--narrow" style={{ margin: 0 }}>
                      Digital catalog listings could not be reached. Try again shortly.
                    </p>
                  </div>
                ) : filteredDigitalRows.length > 0 ? (
                  <div className="lib-digital-grid">
                    {filteredDigitalRows.map(({ asset: a, edition: p, digitalEditionCount }) => {
                      const editionId = p._id != null ? String(p._id) : "";
                      const ownedDigital = Boolean(editionId && ownedDigitalEditionIds.has(editionId));
                      const img = resolveAssetImage(a);
                      const coverUrl = img !== HERO_FALLBACK ? img : DIGITAL_CARD_IMAGE_FALLBACK;
                      const isBook = p.tierKey === "digital_book";
                      const detailSlug = String(a.slug || "").trim().toLowerCase();
                      const coverStateKey = editionId || detailSlug || String(a._id || "");
                      const isPurchasing = purchasingEditionId === editionId;
                      const displayCoverUrl =
                        digitalCoverBrokenByEdition[coverStateKey] ? DIGITAL_CARD_IMAGE_FALLBACK : coverUrl;
                      const saleOn = p.saleActive !== false;
                      const priceOk = Number.isFinite(Number(p.priceUsd)) && Number(p.priceUsd) > 0;

                      let digitalCtaLabel = isBook ? "Buy Digital Book" : "Buy Digital Copy";
                      let digitalCtaDisabled = !editionId || isPurchasing;
                      let digitalCtaBusy = isPurchasing;
                      if (!editionId) {
                        digitalCtaLabel = "Unavailable — inventory not ready";
                        digitalCtaDisabled = true;
                        digitalCtaBusy = false;
                      } else if (!ownedDigital && !saleOn) {
                        digitalCtaLabel = "Coming soon";
                        digitalCtaDisabled = true;
                        digitalCtaBusy = false;
                      } else if (!ownedDigital && !priceOk) {
                        digitalCtaLabel = "Edition not on sale yet";
                        digitalCtaDisabled = true;
                        digitalCtaBusy = false;
                      } else if (!ownedDigital && isPurchasing) {
                        digitalCtaLabel = "Preparing checkout…";
                        digitalCtaDisabled = true;
                        digitalCtaBusy = true;
                      }

                      return (
                        <article key={detailSlug || a._id} className="lib-digital-card">
                          <div className="lib-digital-card__media">
                            <div className="lib-digital-card__frame" aria-hidden>
                              <div className="lib-digital-card__frame-glow" />
                              <img
                                className="lib-digital-card__cover-img"
                                src={displayCoverUrl}
                                alt={`${a.title} cover`}
                                loading="lazy"
                                decoding="async"
                                onError={() => {
                                  if (!coverStateKey) return;
                                  setDigitalCoverBrokenByEdition((prev) =>
                                    prev[coverStateKey] ? prev : { ...prev, [coverStateKey]: true }
                                  );
                                }}
                              />
                            </div>
                            {digitalCoverBrokenByEdition[coverStateKey] ? (
                              <p className="lib-digital-card__cover-fallback-note">Cover art unavailable</p>
                            ) : null}
                          </div>
                          <div className="lib-digital-card__body">
                            <ul className="lib-digital-card__badges" aria-label="Edition tags">
                              <li className="lib-digital-card__badge lib-digital-card__badge--accent">Digital</li>
                              <li className="lib-digital-card__badge">{isBook ? "Book" : "Comic"}</li>
                              <li className="lib-digital-card__badge lib-digital-card__badge--muted">DRM-free reader</li>
                              {digitalEditionCount > 1 ? (
                                <li className="lib-digital-card__badge lib-digital-card__badge--count">
                                  {digitalEditionCount} editions
                                </li>
                              ) : null}
                            </ul>
                            <h3 className="lib-digital-card__title lib-serif">{a.title}</h3>
                            {p.title ? <p className="lib-digital-card__tier">{p.title}</p> : null}
                            <p className="lib-digital-card__desc">
                              {a.shortDescription || a.description || p.accessSummary || ""}
                            </p>
                            <div className="lib-digital-card__commerce">
                              <div className="lib-digital-card__price-block">
                                <span className="lib-digital-card__price-label">Instant access · digital reader</span>
                                <p className="lib-digital-card__price">{formatUsd(p.priceUsd)}</p>
                              </div>
                              <div className="lib-digital-card__actions">
                                {ownedDigital ? (
                                  <Link
                                    to="/dashboard/library"
                                    className="lib-btn lib-btn--digital-owned"
                                    style={{ textDecoration: "none" }}
                                  >
                                    Owned — open in library
                                  </Link>
                                ) : (
                                  <button
                                    type="button"
                                    className="lib-btn lib-btn--digital-buy"
                                    disabled={digitalCtaDisabled}
                                    aria-busy={digitalCtaBusy ? "true" : undefined}
                                    onClick={() =>
                                      startDigitalCheckout(editionId, {
                                        assetSlug: detailSlug,
                                        assetId: String(a._id || ""),
                                      })
                                    }
                                  >
                                    {digitalCtaLabel}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="lib-btn lib-btn--digital-details"
                                  onClick={() => {
                                    trackLibraryAssetView({
                                      cta_id: "digital_card_view_details",
                                      section: "digital_editions",
                                      asset_slug: detailSlug,
                                    });
                                    navigate(`/library/${encodeURIComponent(detailSlug)}`);
                                  }}
                                >
                                  View Details
                                </button>
                              </div>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="lib-catalog-state" style={{ padding: "28px 0 48px", textAlign: "center" }}>
                    <p className="lib-sec-desc lib-sec-desc--narrow" style={{ margin: 0 }}>
                      Digital releases coming soon.
                    </p>
                  </div>
                )}
              </section>
            ) : null}

            {catalogReady && showUpcomingSection ? (
              <section className="lib-sec lib-sec--upcoming-v2" id="store-upcoming" aria-labelledby="store-upcoming-heading">
                <header className="lib-sec-head lib-sec-head--upcoming-v2">
                  <span className="lib-sec-eyebrow lib-sec-eyebrow--blue">Archive horizon</span>
                  <h2 id="store-upcoming-heading" className="lib-sec-title lib-serif">
                    Upcoming titles
                  </h2>
                  <p className="lib-sec-desc lib-sec-desc--upcoming-v2">
                    Future saga drops curated in the Treydora vault — reserve your attention before the gates open. Mark
                    titles you want on your radar; lore previews go live as each release nears.
                  </p>
                </header>
                {upcomingSorted.length > 0 ? (
                  <div className="lib-upcoming-stack">
                    {upcomingFeatured ? (
                      <UpcomingVaultCard
                        asset={upcomingFeatured}
                        variant="feature"
                        navigate={navigate}
                        buildNotifyMailto={buildLibraryNotifyMailto}
                        resolveBackdrop={resolveUpcomingBackdrop}
                        getTitle={upcomingDisplayTitle}
                        getTeaser={upcomingTeaser}
                      />
                    ) : null}
                    {upcomingGridItems.length ? (
                      <div
                        className={`lib-upcoming-grid${upcomingFeatured ? " lib-upcoming-grid--with-feature" : ""}`}
                      >
                        {upcomingGridItems.map((u) => (
                          <UpcomingVaultCard
                            key={String(u.slug || u._id)}
                            asset={u}
                            variant="grid"
                            navigate={navigate}
                            buildNotifyMailto={buildLibraryNotifyMailto}
                            resolveBackdrop={resolveUpcomingBackdrop}
                            getTitle={upcomingDisplayTitle}
                            getTeaser={upcomingTeaser}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="lib-upcoming-empty">
                    <div className="lib-upcoming-empty__glow" aria-hidden />
                    <p className="lib-upcoming-empty__eyebrow">Vault horizon</p>
                    <h3 className="lib-upcoming-empty__title lib-serif">The next chronicles are being bound</h3>
                    <p className="lib-upcoming-empty__copy">
                      No public upcoming releases are listed yet. When the archive opens new saga slots, they will appear
                      here with cinematic art and notify options.
                    </p>
                  </div>
                )}
              </section>
            ) : null}
        </>
      </div>
      <LibraryPurchaseLegalModal
        isOpen={libraryLegalModalOpen}
        onClose={() => {
          setLibraryLegalModalOpen(false);
          libraryLegalPendingRef.current = null;
        }}
        purchaseKind={libraryLegalModalKind}
        showMerchCheckbox={libraryLegalModalKind === "nft" && showMerchLegalCheckbox}
        legalAck={legalAck}
        onValidityChange={setLegalAck}
        onContinue={handleLibraryLegalModalContinue}
      />
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        initialMessage="Log in or create an account to continue checkout. Your purchase is tied to your Treydora library after payment."
        loginSuccessMessage="Signed in. Starting secure checkout…"
        registerSuccessMessage="Account ready. Starting secure checkout…"
      />
    </div>
  );
}
